import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import BorrowingForm from '@components/common/forms/BorrowingForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const BorrowingBorrowPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    const [borrowing, setBorrowing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBorrowing = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/borrowings/${id}`);
                setBorrowing(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch borrowing:', err);
                setError(t('borrowing.borrowingBorrow.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchBorrowing();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            await api.borrowings.borrow(id, {
                borrowed_at: formData.borrowed_at || new Date().toISOString(),
                borrow_notes: formData.notes, // Map 'notes' to 'borrow_notes'
                expected_return_at: formData.expected_return_at
            });
            notify.success(t('borrowing.borrowingBorrow.borrowed_successfully'));
            navigate(`/borrowings/${id}`);
        } catch (err) {
            console.error('Failed to borrow resource:', err);
            notify.error(t('borrowing.borrowingBorrow.borrowError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/borrowings/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('borrowing.borrowingBorrow.borrowResource')}
                subtitle={t('borrowing.borrowingBorrow.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !borrowing) {
        return (
            <FormPageLayout
                title={t('borrowing.borrowingBorrow.borrowResource')}
                subtitle={error || t('borrowing.borrowingBorrow.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('borrowing.borrowingBorrow.borrowResource')}
            subtitle={t('borrowing.borrowingBorrow.subtitle', { resourceName: borrowing.resource?.name })}
            onBack={handleCancel}
        >
            <BorrowingForm
                borrowing={borrowing}
                resource={borrowing.resource}
                mode="borrow"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </FormPageLayout>
    );
};

export default BorrowingBorrowPage;
